import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirDimensionType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import { useSettings } from 'context/SettingsContext';
import { formatQty } from '../../../ObjectCard/ProposalShortage/model/formatQty';
import { tDirName } from 'utils/helpers/i18n/tDirName';

export interface TeamShortageRow {
    productId?: number;
    serviceId?: number;
    name: string;
    article?: string;
    dimensionName?: string;
    need: number;
    have: number;
    deficit: number;
}

interface TeamShortageTableProps {
    kind: 'products' | 'services';
    items: TeamShortageRow[];
    canAdd: boolean;
    addingKey?: string;
    onAdd: (item: TeamShortageRow) => void;
}

export const teamShortageKey = (kind: 'products' | 'services', item: TeamShortageRow) =>
    kind === 'products'
        ? `product-${item.productId || 0}`
        : `service-${item.serviceId || 0}-${item.name}`;

const TeamShortageTable = ({
    kind,
    items,
    canAdd,
    addingKey,
    onAdd,
}: TeamShortageTableProps) => {
    const { t } = useTranslation(['Objects', 'Products']);
    const { localSettings } = useSettings();

    return (
        <div className="proposal-shortage__table-wrap">
            <Table size="sm" className="proposal-shortage__table mb-0 align-middle">
                <thead>
                    <tr>
                        <th>{t('proposalShortage.colName')}</th>
                        <th className="text-end">{t('proposalShortage.colNeed')}</th>
                        <th className="text-end">{t('proposalShortage.colHave')}</th>
                        <th className="text-end">{t('proposalShortage.colDeficit')}</th>
                        {canAdd && (
                            <th className="text-end">{t('proposalShortage.colActions')}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => {
                        const key = teamShortageKey(kind, item);
                        const dim = localSettings?.DIRS?.Dimension?.find(
                            (d: DirDimensionType) => d.dimensionName === item.dimensionName,
                        );
                        const dimensionLabel = tDirName(t, dim?.i18nKey, item.dimensionName);
                        const missing = !(Number(item.have) > 0);
                        const addLabel = t('proposalShortage.btnAdd');
                        return (
                            <tr
                                key={key}
                                className={`proposal-shortage__tr proposal-shortage__tr--${
                                    missing ? 'missing' : 'insufficient'
                                }`}
                            >
                                <td className="proposal-shortage__name-cell">
                                    <div className="text-truncate" title={item.name}>
                                        {item.name}
                                    </div>
                                    {(item.article || dimensionLabel) && (
                                        <div className="proposal-shortage__meta text-truncate">
                                            {[item.article, dimensionLabel].filter(Boolean).join(' · ')}
                                        </div>
                                    )}
                                </td>
                                <td className="text-end text-nowrap">{formatQty(item.need)}</td>
                                <td className="text-end text-nowrap">{formatQty(item.have)}</td>
                                <td className="text-end text-nowrap proposal-shortage__deficit">
                                    −{formatQty(item.deficit)}
                                </td>
                                {canAdd && (
                                    <td className="text-end proposal-shortage__actions-cell">
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            className="proposal-shortage__add-btn p-0"
                                            disabled={addingKey === key}
                                            title={addLabel}
                                            aria-label={addLabel}
                                            onClick={() => onAdd(item)}
                                        >
                                            <SolarIcon name="plus-lg" aria-hidden="true" />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

export default TeamShortageTable;
